import { useModalFocusTrap } from "./useModalFocusTrap";
import { X } from "lucide-react";
import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import { IbkrAccountSummary } from "../types";

interface AdminAccountDetailModalProps {
  appearance?: "default" | "alpha";
  isOpen: boolean;
  onClose: () => void;
  summary: IbkrAccountSummary;
  userName?: string;
}

const AdminAccountDetailModal: React.FC<AdminAccountDetailModalProps> = ({
  appearance = "default",
  isOpen,
  onClose,
  summary,
  userName,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const dialogRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  useModalFocusTrap({ dialogRef, isOpen, onClose });
  if (!isOpen) return null;

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "N/A";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatPercent = (value: string | null | undefined) => {
    if (!value) return "N/A";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return `${(num * 100).toFixed(2)}%`;
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  // Parse settled cash by date string
  const parseSettledCash = (settledStr: string | null | undefined) => {
    if (!settledStr) return [];
    return settledStr.split(";").map((entry) => {
      const [date, amount] = entry.split(":");
      const formattedDate = date
        ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
        : date;
      return { date: formattedDate, amount: parseFloat(amount) };
    });
  };

  // Helper to get payload value
  const getPayloadValue = (key: string): { value: string | null; amount: number } | null => {
    if (!summary.ibkrPayload || !summary.ibkrPayload[key]) return null;
    return summary.ibkrPayload[key];
  };

  const getPayloadAmount = (key: string): number | null => {
    const item = getPayloadValue(key);
    return item?.amount ?? null;
  };

  const getPayloadStringValue = (key: string): string | null => {
    const item = getPayloadValue(key);
    return item?.value ?? null;
  };

  const settledCash = parseSettledCash(summary.settledCashByDate);

  // Section component for organization
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className={appearance === "alpha"? `mb-6 rounded-v7-panel border border-v7-ink/15 bg-v7-white` : `mb-6 rounded-lg border ${isDarkMode ?"border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}>
      <div className={appearance === "alpha"? `px-4 py-2 border-b border-v7-ink/15 bg-v7-paper` : `px-4 py-2 border-b ${isDarkMode ?"border-gray-700 bg-gray-700/50" : "border-gray-200 bg-gray-50"}`}>
        <h3 className={appearance === "alpha"? `font-semibold text-sm uppercase tracking-wide text-v7-bronze-dark` : `font-semibold text-sm uppercase tracking-wide ${isDarkMode ?"text-teal-400" : "text-teal-600"}`}>
          {title}
        </h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  // Metric row component
  const MetricRow: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({
    label,
    value,
    highlight,
  }) => (
    <div className={appearance === "alpha" ? "flex justify-between items-center py-1.5 border-b border-v7-ink/15 last:border-0" : "flex justify-between items-center py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0"}>
      <span className={appearance === "alpha"? `text-sm text-v7-ink-soft` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-600"}`}>{label}</span>
      <span
        className={appearance === "alpha" ? `font-mono text-sm ${highlight
    ? "text-v7-success font-semibold" : "text-v7-ink"}` : `font-mono text-sm ${
          highlight
            ? isDarkMode
              ? "text-green-400 font-semibold"
              : "text-green-600 font-semibold"
            : isDarkMode
            ? "text-white"
            : "text-gray-900"
        }`}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className={appearance === "alpha" ? "fixed inset-0 bg-v7-ink/60 backdrop-blur-sm" : "fixed inset-0 bg-black/60 backdrop-blur-sm"} onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 flex items-center justify-center">
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-appearance={appearance}
          className={appearance === "alpha" ? `relative w-full h-full max-w-7xl rounded-v7-popover border border-v7-ink/15 font-v7-sans text-v7-ink shadow-2xl overflow-hidden flex flex-col bg-v7-paper` : `relative w-full h-full max-w-7xl rounded-xl shadow-2xl overflow-hidden flex flex-col ${
            isDarkMode ? "bg-gray-900" : "bg-gray-50"
          }`}
        >
          {/* Header */}
          <div
            className={appearance === "alpha" ? `flex-shrink-0 px-6 py-4 border-b border-v7-ink/15 bg-v7-white` : `flex-shrink-0 px-6 py-4 border-b ${
              isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h2 id={titleId} className={appearance === "alpha"? `text-xl font-bold text-v7-ink` : `text-xl font-bold ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                    IBKR Account Detail
                  </h2>
                  <p className={appearance === "alpha"? `text-sm text-v7-ink/60` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                    {userName && `${userName} - `}
                    {summary.accountCode} ({summary.accountType})
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={appearance === "alpha" ? `px-2 py-1 rounded-v7-control text-xs font-medium ${summary.accountReady
    ? "bg-v7-success/10 text-v7-success" : "bg-v7-rust/5 text-v7-rust"}` : `px-2 py-1 rounded text-xs font-medium ${
                      summary.accountReady
                        ? isDarkMode
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-100 text-green-700"
                        : isDarkMode
                        ? "bg-red-900/30 text-red-400"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {summary.accountReady ? "READY" : "NOT READY"}
                  </span>
                  <span
                    className={appearance === "alpha" ? `px-2 py-1 rounded-v7-control text-xs font-mono bg-v7-mist text-v7-ink-soft` : `px-2 py-1 rounded text-xs font-mono ${
                      isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {summary.tradingTypeSecurity}
                  </span>
                </div>
              </div>
              <button
                aria-label="Close account details"
                onClick={onClose}
                className={appearance === "alpha" ? `p-2 rounded-v7-panel transition-colors hover:bg-v7-paper text-v7-ink/60` : `p-2 rounded-lg transition-colors ${
                  isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={appearance === "alpha"? `mt-2 text-xs text-v7-ink/60` : `mt-2 text-xs ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
              Last Updated: {formatDate(summary.updatedDatetimeUtc)} | Data Timestamp:{" "}
              {formatTimestamp(summary.timestamp)}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* Key Metrics */}
              <Section title="Key Account Metrics">
                <MetricRow label="Net Liquidation" value={formatCurrency(summary.netLiquidationSecurity)} highlight />
                <MetricRow label="Buying Power" value={formatCurrency(summary.buyingPower)} highlight />
                <MetricRow label="Available Funds" value={formatCurrency(summary.availableFundsSecurity)} />
                <MetricRow label="Excess Liquidity" value={formatCurrency(summary.excessLiquiditySecurity)} />
                <MetricRow label="Equity with Loan" value={formatCurrency(summary.equityWithLoanValueSecurity)} />
                <MetricRow label="Gross Position Value" value={formatCurrency(summary.grossPositionValueSecurity)} />
              </Section>

              {/* Cash Values */}
              <Section title="Cash Values">
                <MetricRow label="Total Cash Value" value={formatCurrency(summary.totalCashValueSecurity)} />
                <MetricRow label="Accrued Cash" value={formatCurrency(summary.accruedCashSecurity)} />
                <MetricRow label="Accrued Dividend" value={formatCurrency(summary.accruedDividendSecurity)} />
                <MetricRow label="Billable" value={formatCurrency(summary.billableSecurity)} />
                <MetricRow label="Guarantee" value={formatCurrency(summary.guaranteeSecurity)} />
              </Section>

              {/* Margin Requirements */}
              <Section title="Margin Requirements">
                <MetricRow label="Init Margin Req" value={formatCurrency(summary.initMarginReqSecurity)} />
                <MetricRow label="Maint Margin Req" value={formatCurrency(summary.maintenanceMarginReqSecurity)} />
                <MetricRow label="Full Init Margin" value={formatCurrency(summary.fullInitMarginReqSecurity)} />
                <MetricRow label="Full Maint Margin" value={formatCurrency(summary.fullMaintMarginReqSecurity)} />
                <MetricRow label="Full Available Funds" value={formatCurrency(summary.fullAvailableFundsSecurity)} />
                <MetricRow label="Full Excess Liquidity" value={formatCurrency(summary.fullExcessLiquiditySecurity)} />
              </Section>

              {/* RegT & SMA */}
              {summary.ibkrPayload && (
                <Section title="RegT & SMA">
                  <MetricRow label="SMA" value={formatCurrency(getPayloadAmount("sma"))} />
                  <MetricRow label="SMA (Securities)" value={formatCurrency(getPayloadAmount("sma-s"))} />
                  <MetricRow label="RegT Equity" value={formatCurrency(getPayloadAmount("regtequity"))} />
                  <MetricRow label="RegT Equity (Securities)" value={formatCurrency(getPayloadAmount("regtequity-s"))} />
                  <MetricRow label="RegT Margin" value={formatCurrency(getPayloadAmount("regtmargin"))} />
                  <MetricRow label="RegT Margin (Securities)" value={formatCurrency(getPayloadAmount("regtmargin-s"))} />
                </Section>
              )}

              {/* Risk Metrics */}
              {summary.ibkrPayload && (
                <Section title="Risk Metrics">
                  <MetricRow label="Cushion" value={formatPercent(getPayloadStringValue("cushion"))} highlight />
                  <MetricRow label="Leverage (Securities)" value={getPayloadStringValue("leverage-s") || "N/A"} />
                  <MetricRow
                    label="Previous Day Equity"
                    value={formatCurrency(getPayloadAmount("previousdayequitywithloanvalue"))}
                  />
                  <MetricRow
                    label="Net Liquidation Uncertainty"
                    value={formatCurrency(getPayloadAmount("netliquidationuncertainty"))}
                  />
                  <MetricRow
                    label="Highest Severity"
                    value={getPayloadStringValue("highestseverity") || "None"}
                  />
                </Section>
              )}

              {/* Day Trading */}
              {summary.ibkrPayload && (
                <Section title="Day Trading">
                  <MetricRow
                    label="Day Trades Remaining"
                    value={getPayloadStringValue("daytradesremaining") || "N/A"}
                  />
                  <MetricRow
                    label="Day Trades T+1"
                    value={getPayloadStringValue("daytradesremainingt+1") || "N/A"}
                  />
                  <MetricRow
                    label="Day Trades T+2"
                    value={getPayloadStringValue("daytradesremainingt+2") || "N/A"}
                  />
                  <MetricRow
                    label="Day Trades T+3"
                    value={getPayloadStringValue("daytradesremainingt+3") || "N/A"}
                  />
                  <MetricRow
                    label="Day Trades T+4"
                    value={getPayloadStringValue("daytradesremainingt+4") || "N/A"}
                  />
                  <MetricRow
                    label="Day Trading Status"
                    value={
                      <span className="text-xs break-all max-w-[200px] inline-block text-right">
                        {getPayloadStringValue("daytradingstatus-s") || "N/A"}
                      </span>
                    }
                  />
                </Section>
              )}

              {/* Lookahead Values */}
              {summary.ibkrPayload && (
                <Section title="Lookahead Values">
                  <MetricRow
                    label="Lookahead Init Margin"
                    value={formatCurrency(getPayloadAmount("lookaheadinitmarginreq"))}
                  />
                  <MetricRow
                    label="Lookahead Maint Margin"
                    value={formatCurrency(getPayloadAmount("lookaheadmaintmarginreq"))}
                  />
                  <MetricRow
                    label="Lookahead Available Funds"
                    value={formatCurrency(getPayloadAmount("lookaheadavailablefunds"))}
                  />
                  <MetricRow
                    label="Lookahead Excess Liquidity"
                    value={formatCurrency(getPayloadAmount("lookaheadexcessliquidity"))}
                  />
                  <MetricRow
                    label="Next Change Timestamp"
                    value={getPayloadStringValue("lookaheadnextchange") || "N/A"}
                  />
                </Section>
              )}

              {/* Post Expiration */}
              {summary.ibkrPayload && (
                <Section title="Post Expiration">
                  <MetricRow
                    label="Post Expiration Excess"
                    value={formatCurrency(getPayloadAmount("postexpirationexcess"))}
                  />
                  <MetricRow
                    label="Post Expiration Excess (C)"
                    value={formatCurrency(getPayloadAmount("postexpirationexcess-c"))}
                  />
                  <MetricRow
                    label="Post Expiration Excess (S)"
                    value={formatCurrency(getPayloadAmount("postexpirationexcess-s"))}
                  />
                  <MetricRow
                    label="Post Expiration Margin"
                    value={formatCurrency(getPayloadAmount("postexpirationmargin"))}
                  />
                  <MetricRow
                    label="Post Expiration Margin (C)"
                    value={formatCurrency(getPayloadAmount("postexpirationmargin-c"))}
                  />
                  <MetricRow
                    label="Post Expiration Margin (S)"
                    value={formatCurrency(getPayloadAmount("postexpirationmargin-s"))}
                  />
                </Section>
              )}

              {/* Commodities Segment */}
              {summary.ibkrPayload && (
                <Section title="Commodities Segment">
                  <MetricRow
                    label="Segment Title"
                    value={getPayloadStringValue("segmenttitle-c") || "N/A"}
                  />
                  <MetricRow
                    label="Net Liquidation (C)"
                    value={formatCurrency(getPayloadAmount("netliquidation-c"))}
                  />
                  <MetricRow
                    label="Total Cash (C)"
                    value={formatCurrency(getPayloadAmount("totalcashvalue-c"))}
                  />
                  <MetricRow
                    label="Init Margin (C)"
                    value={formatCurrency(getPayloadAmount("initmarginreq-c"))}
                  />
                  <MetricRow
                    label="Maint Margin (C)"
                    value={formatCurrency(getPayloadAmount("maintmarginreq-c"))}
                  />
                  <MetricRow
                    label="Available Funds (C)"
                    value={formatCurrency(getPayloadAmount("availablefunds-c"))}
                  />
                  <MetricRow
                    label="Excess Liquidity (C)"
                    value={formatCurrency(getPayloadAmount("excessliquidity-c"))}
                  />
                </Section>
              )}

              {/* Securities Segment */}
              {summary.ibkrPayload && (
                <Section title="Securities Segment">
                  <MetricRow
                    label="Segment Title"
                    value={getPayloadStringValue("segmenttitle-s") || "N/A"}
                  />
                  <MetricRow
                    label="Net Liquidation (S)"
                    value={formatCurrency(getPayloadAmount("netliquidation-s"))}
                  />
                  <MetricRow
                    label="Total Cash (S)"
                    value={formatCurrency(getPayloadAmount("totalcashvalue-s"))}
                  />
                  <MetricRow
                    label="Init Margin (S)"
                    value={formatCurrency(getPayloadAmount("initmarginreq-s"))}
                  />
                  <MetricRow
                    label="Maint Margin (S)"
                    value={formatCurrency(getPayloadAmount("maintmarginreq-s"))}
                  />
                  <MetricRow
                    label="Available Funds (S)"
                    value={formatCurrency(getPayloadAmount("availablefunds-s"))}
                  />
                  <MetricRow
                    label="Excess Liquidity (S)"
                    value={formatCurrency(getPayloadAmount("excessliquidity-s"))}
                  />
                </Section>
              )}

              {/* Other Metrics */}
              {summary.ibkrPayload && (
                <Section title="Other Metrics">
                  <MetricRow
                    label="PA Shares Value"
                    value={formatCurrency(getPayloadAmount("pasharesvalue"))}
                  />
                  <MetricRow
                    label="PA Shares Value (C)"
                    value={formatCurrency(getPayloadAmount("pasharesvalue-c"))}
                  />
                  <MetricRow
                    label="PA Shares Value (S)"
                    value={formatCurrency(getPayloadAmount("pasharesvalue-s"))}
                  />
                  <MetricRow
                    label="Indian Stock Haircut"
                    value={formatCurrency(getPayloadAmount("indianstockhaircut"))}
                  />
                  <MetricRow
                    label="Physical Certificate Value"
                    value={formatCurrency(getPayloadAmount("physicalcertificatevalue"))}
                  />
                  <MetricRow
                    label="Incentive Coupons"
                    value={formatCurrency(getPayloadAmount("incentivecoupons"))}
                  />
                  <MetricRow
                    label="Total Debit Card Pending"
                    value={formatCurrency(getPayloadAmount("totaldebitcardpendingcharges"))}
                  />
                </Section>
              )}

              {/* Account Flags */}
              <Section title="Account Flags">
                <MetricRow
                  label="Account Ready"
                  value={
                    <span className={appearance === "alpha" ? summary.accountReady ? "text-v7-success" : "text-v7-rust" : summary.accountReady ? "text-green-500" : "text-red-500"}>
                      {summary.accountReady ? "Yes" : "No"}
                    </span>
                  }
                />
                <MetricRow
                  label="NLV & Margin In Review"
                  value={
                    <span className={appearance === "alpha" ? summary.nlvAndMarginInReview ? "text-v7-bronze-dark" : "text-v7-success" : summary.nlvAndMarginInReview ? "text-yellow-500" : "text-green-500"}>
                      {summary.nlvAndMarginInReview ? "Yes" : "No"}
                    </span>
                  }
                />
                <MetricRow
                  label="What-If PM Enabled"
                  value={
                    <span className={appearance === "alpha" ? summary.whatIfPmEnabled ? "text-v7-success" : "text-v7-ink/60" : summary.whatIfPmEnabled ? "text-green-500" : "text-gray-500"}>
                      {summary.whatIfPmEnabled ? "Yes" : "No"}
                    </span>
                  }
                />
                <MetricRow
                  label="Deposit on Credit Hold"
                  value={getPayloadStringValue("depositoncredithold") || "N/A"}
                />
              </Section>

              {/* Settled Cash by Date */}
              <Section title="Settled Cash by Date">
                {settledCash.length > 0 ? (
                  settledCash.map((entry, idx) => (
                    <MetricRow key={idx} label={entry.date} value={formatCurrency(entry.amount)} />
                  ))
                ) : (
                  <p className={appearance === "alpha"? `text-sm text-v7-ink/60` : `text-sm ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                    No settlement data available
                  </p>
                )}
              </Section>
            </div>

            {/* Raw IBKR Payload (Collapsible) */}
            {summary.ibkrPayload && (
              <details className={appearance === "alpha"? `mt-6 rounded-v7-panel border border-v7-ink/15` : `mt-6 rounded-lg border ${isDarkMode ?"border-gray-700" : "border-gray-200"}`}>
                <summary
                  className={appearance === "alpha" ? `px-4 py-3 cursor-pointer font-medium text-sm bg-v7-paper text-v7-ink-soft hover:bg-v7-paper` : `px-4 py-3 cursor-pointer font-medium text-sm ${
                    isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Raw IBKR Payload (Debug)
                </summary>
                <pre
                  className={appearance === "alpha" ? `p-4 text-xs overflow-auto max-h-96 bg-v7-paper text-v7-ink-soft` : `p-4 text-xs overflow-auto max-h-96 ${
                    isDarkMode ? "bg-gray-900 text-gray-300" : "bg-gray-50 text-gray-700"
                  }`}
                >
                  {JSON.stringify(summary.ibkrPayload, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountDetailModal;

